import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { type } from "arktype";
import { Link2, Loader2, UserX } from "lucide-react";
import { Controller, useForm, useWatch } from "react-hook-form";

import type { MedicoFila } from "@/api/db/medico";
import { Button } from "@/components/ui/button";
import { useMedicoRelink } from "@/hooks/use-medico";
import { MedicoLabels } from "@/lib/labels/medico";
import { cn } from "@/lib/utils";

import { Modal } from "@/components/ui/modal";
import { PersonSearch, PersonSearchSchemas } from "./person-search";

type Anchor = MedicoFila["orphansList"][number];
type Kind = "orphan" | "awaiting";

const relinkForm = type({
	chosen: PersonSearchSchemas.chosen.or("null"),
}).narrow(({ chosen }, ctx) => chosen?.kind === "patient" || ctx.mustBe("a patient"));

function RelinkModal({
	anchor,
	kind,
	onClose,
}: {
	anchor: Anchor;
	kind: Kind;
	onClose: () => void;
}) {
	const relink = useMedicoRelink();
	const {
		control,
		handleSubmit,
		formState: { isValid },
	} = useForm<typeof relinkForm.infer>({
		resolver: arktypeResolver(relinkForm),
		mode: "onChange",
		defaultValues: { chosen: null },
	});
	const chosen = useWatch({ control, name: "chosen" });
	const isLead = chosen?.kind === "lead";
	const submit = handleSubmit(({ chosen }) => {
		if (chosen?.kind !== "patient") {
			return;
		}

		relink.mutate(
			{ medPatientId: anchor.medPatientId, patientId: chosen.patientId },
			{ onSuccess: onClose },
		);
	});

	return (
		<Modal
			title={MedicoLabels.gate.modalTitle}
			description={anchor.nameSnapshot}
			onClose={onClose}
			isPending={relink.isPending}
		>
			<form onSubmit={submit} className="flex flex-col gap-4">
				<p className="text-xs leading-relaxed text-muted-foreground">
					{MedicoLabels.gate.explanation[kind]}
				</p>

				<Controller
					control={control}
					name="chosen"
					render={({ field }) => (
						<PersonSearch
							chosen={field.value}
							onChoose={field.onChange}
							initialSearch={anchor.nameSnapshot}
							disabled={relink.isPending}
						/>
					)}
				/>

				{isLead && (
					<p className="rounded-md border border-warning/30 bg-warning/5 px-2.5 py-1.5 text-xs text-foreground">
						{MedicoLabels.gate.relinkLeadHint}
					</p>
				)}

				<div className="mt-1 flex items-center justify-end gap-2">
					<Button
						type="button"
						size="sm"
						variant="secondary"
						disabled={relink.isPending}
						onClick={onClose}
					>
						Cancelar
					</Button>

					<Button
						size="sm"
						type="submit"
						variant="default"
						className="px-4"
						disabled={!isValid || relink.isPending}
					>
						{relink.isPending && <Loader2 className="animate-spin" />}
						{!relink.isPending && <Link2 />}
						{relink.isPending && "vinculando prontuário..."}
						{!relink.isPending && MedicoLabels.gate.action}
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function AnchorRow({ anchor, kind }: { anchor: Anchor; kind: Kind }) {
	const [linking, setLinking] = useState(false);

	return (
		<li className="flex items-center gap-3 py-2.5 last:pb-0">
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-medium text-foreground">{anchor.nameSnapshot}</p>

				{anchor.birthSnapshot !== null && (
					<p className="text-xs text-muted-foreground">nasc. {anchor.birthSnapshot}</p>
				)}
			</div>

			<span
				className={cn(
					"shrink-0 rounded border px-1.5 py-0.5 text-xs font-medium leading-none",
					kind === "orphan" && "border-destructive/30 bg-destructive/10 text-destructive",
					kind === "awaiting" && "border-warning/30 bg-warning/10 text-warning",
				)}
			>
				{MedicoLabels.gate.tag[kind]}
			</span>

			<Button type="button" size="sm" variant="secondary" onClick={() => setLinking(true)}>
				<Link2 />
				{MedicoLabels.gate.action}
			</Button>

			{linking && <RelinkModal anchor={anchor} kind={kind} onClose={() => setLinking(false)} />}
		</li>
	);
}

export function GateBanner({ orphans, awaiting }: { orphans: Anchor[]; awaiting: Anchor[] }) {
	if (orphans.length === 0 && awaiting.length === 0) {
		return null;
	}

	const rows = [
		...orphans.map((anchor) => ({ anchor, kind: "orphan" as const })),
		...awaiting.map((anchor) => ({ anchor, kind: "awaiting" as const })),
	];

	return (
		<section data-component="gate-banner" className="rounded-lg bg-card p-4 shadow-card">
			<div className="flex items-center gap-3">
				<span className="grid size-[38px] shrink-0 place-items-center rounded-md bg-destructive-soft text-destructive">
					<UserX className="size-4" />
				</span>

				<div className="min-w-0">
					<h2 className="text-sm font-semibold text-foreground">{MedicoLabels.gate.title}</h2>

					<p className="text-xs text-muted-foreground">{MedicoLabels.gate.subtitle}</p>
				</div>
			</div>

			<ul className="mt-3 flex flex-col divide-y divide-border border-t border-border">
				{rows.map(({ anchor, kind }) => (
					<AnchorRow key={anchor.medPatientId} anchor={anchor} kind={kind} />
				))}
			</ul>
		</section>
	);
}
